"use strict";

/*
  Site Cart
*/

class SiteCart extends HTMLElement {
  constructor() {
    super();
    this.cart = null;
    this.rewardDebounceTimers = new Map();
    this.pendingRewardOperations = new Set();
    this.rewardOperationQueue = Promise.resolve();
    this.checkoutGuardActive = false;
    this.reconcileRewardsPromise = null;
    this.pendingReconcileOptions = null;
    this.externalCartRefreshPromise = null;
    this.pendingExternalCartRefreshOptions = null;
    this.pendingCartMutations = 0;
    this.pendingCartBusyMutations = 0;
    this.cartTransactionQueue = Promise.resolve();
  }

  connectedCallback() {
    this.cart = document.querySelector(".site-cart");
    this.bindMultiplierOffer();
    this.initRewardsOnLoad();
    this.installCheckoutGuard();
    this.installBfcacheCheckoutRestore();
    SiteCart.installExternalCartRefreshListener();
  }

  installBfcacheCheckoutRestore() {
    if (SiteCart.__bfcacheCheckoutRestoreInstalled) return;
    SiteCart.__bfcacheCheckoutRestoreInstalled = true;

    // When the user navigates back from checkout, the page is often restored
    // from the back-forward cache with the checkout guard still engaged and
    // the checkout button left disabled. Reset both so checkout stays usable.
    window.addEventListener("pageshow", () => {
      const siteCart = document.querySelector("site-cart");
      if (!siteCart) return;
      siteCart.checkoutGuardActive = false;
      if (typeof siteCart.restoreCheckoutLinkBusy === "function") {
        try {
          siteCart.restoreCheckoutLinkBusy();
        } catch (e) {
          // Best-effort restore.
        }
        siteCart.restoreCheckoutLinkBusy = null;
      }
    });
  }

  static installExternalCartRefreshListener() {
    if (SiteCart.externalCartRefreshListenerInstalled) return;
    SiteCart.externalCartRefreshListenerInstalled = true;

    let debounceTimer = null;
    let lastCartSig = null;

    const run = async () => {
      const siteCart = document.querySelector("site-cart");
      if (!siteCart) return;
      const cart = await siteCart.fetchCart().catch(() => null);
      if (!cart) return;
      const sig = siteCart.getCartSignature(cart);
      if (sig === lastCartSig) return;
      lastCartSig = sig;
      await siteCart.refreshCartState({ skipBusyState: true }).catch(() => {});
    };

    const schedule = (e) => {
      const src = e && e.detail && e.detail.source;
      if (src === "site-cart") return;
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        debounceTimer = null;
        run();
      }, 160);
    };

    ["cart:refresh", "cart:updated"].forEach((name) => {
      document.addEventListener(name, schedule);
    });
  }

  applyCartSectionsHtml(siteCartHtml) {
    if (!siteCartHtml) return false;
    const doc = new DOMParser().parseFromString(siteCartHtml, "text/html");
    [
      "#main-cart-header",
      "#main-cart-items",
      "#main-cart-footer",
      "#main-cart-multiplier",
    ].forEach((sel) => {
      const newEl = doc.querySelector(sel);
      if (!newEl) return;
      this.getSectionTargets(sel).forEach((curEl) => {
        curEl.innerHTML = newEl.innerHTML;
      });
    });
    return true;
  }

  getSectionsHtmlFromResponse(result) {
    const sections = result && result.sections ? result.sections : null;
    if (!sections || typeof sections !== "object") return null;
    return sections["site-cart"] || null;
  }

  async renderRefreshedCartSections() {
    const res = await fetch(
      `${window.Shopify.routes.root}?sections=site-cart`,
    );
    if (!res.ok) return;
    const data = await res.json();
    if (!data || typeof data !== "object") return;
    this.applyCartSectionsHtml(data["site-cart"]);
  }

  async refreshCartSections(options = {}) {
    const pending = this.pendingExternalCartRefreshOptions || {};
    this.pendingExternalCartRefreshOptions = {
      skipBusyState: Boolean(pending.skipBusyState || options.skipBusyState),
    };

    if (this.externalCartRefreshPromise) {
      return this.externalCartRefreshPromise;
    }

    this.externalCartRefreshPromise = (async () => {
      try {
        while (this.pendingExternalCartRefreshOptions) {
          const currentOptions = this.pendingExternalCartRefreshOptions;
          this.pendingExternalCartRefreshOptions = null;

          if (currentOptions.skipBusyState) {
            await this.refreshCartState({ skipBusyState: true });
          } else {
            await this.refreshCartState();
          }
        }
      } finally {
        this.externalCartRefreshPromise = null;
      }
    })();

    return this.externalCartRefreshPromise;
  }

  scheduleRewardReconciliation({ delayMs = 120 } = {}) {
    const key = "reconcileRewards";
    const prev = this.rewardDebounceTimers.get(key);
    if (prev) clearTimeout(prev);

    const t = setTimeout(() => {
      this.rewardDebounceTimers.delete(key);
      this.reconcileRewards();
    }, delayMs);
    this.rewardDebounceTimers.set(key, t);
  }

  async reconcileRewards(options = {}) {
    const pending = this.pendingReconcileOptions || {};
    this.pendingReconcileOptions = {
      forceCartRefresh: Boolean(pending.forceCartRefresh || options.forceCartRefresh),
      skipBusyState: Boolean(pending.skipBusyState || options.skipBusyState),
      skipSectionRender: Boolean(
        pending.skipSectionRender || options.skipSectionRender,
      ),
      skipRewardRefresh: Boolean(
        pending.skipRewardRefresh || options.skipRewardRefresh,
      ),
      cartBusy:
        typeof options.cartBusy !== "undefined"
          ? options.cartBusy
          : pending.cartBusy,
    };

    if (this.reconcileRewardsPromise) {
      return this.reconcileRewardsPromise;
    }

    this.reconcileRewardsPromise = (async () => {
      try {
        while (this.pendingReconcileOptions) {
          const currentOptions = this.pendingReconcileOptions;
          this.pendingReconcileOptions = null;

          await this.rewardOperationQueue;

          let cart = await this.fetchCartStable({ attempts: 3, delayMs: 150 });
          if (!cart) cart = await this.fetchCart();

          const removedRewards = await this.removeIneligibleRewards(
            cart,
            currentOptions,
          );
          await this.rewardOperationQueue;

          if (removedRewards) {
            const cartAfter = await this.fetchCartStable({
              attempts: 2,
              delayMs: 150,
            });
            if (cartAfter) {
              await this.removeIneligibleRewards(cartAfter, currentOptions);
              await this.rewardOperationQueue;
            }
          }

          if (currentOptions.forceCartRefresh) {
            await this.refreshCartSections({
              skipBusyState: !!currentOptions.skipBusyState,
            });
          }
        }
      } finally {
        this.reconcileRewardsPromise = null;
      }
    })();

    return this.reconcileRewardsPromise;
  }

  installCheckoutGuard() {
    if (SiteCart.__checkoutGuardInstalled) return;
    SiteCart.__checkoutGuardInstalled = true;

    document.addEventListener(
      "click",
      async (e) => {
        try {
          if (e.defaultPrevented) return;
          if (e.button !== 0) return;
          if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

          const link =
            e.target && e.target.closest ? e.target.closest("a[href]") : null;
          if (!link) return;

          const href = link.getAttribute("href");
          if (!href) return;

          const url = new URL(href, window.location.origin);
          if (url.origin !== window.location.origin) return;
          if (!url.pathname || !url.pathname.endsWith("/checkout")) return;

          const siteCart = document.querySelector("site-cart");
          if (!siteCart) return;

          e.preventDefault();
          e.stopPropagation();

          await siteCart.beginCheckout(url.toString(), link);
        } catch (err) {
          console.error("Checkout guard error:", err);
        }
      },
      { capture: true },
    );

    // Defensive: intercept form-based checkout submissions too.
    document.addEventListener(
      "submit",
      async (e) => {
        try {
          if (e.defaultPrevented) return;
          const form =
            e.target && e.target.closest ? e.target.closest("form") : null;
          if (!form) return;

          const actionAttr = form.getAttribute("action") || "";
          const actionUrl = actionAttr
            ? new URL(actionAttr, window.location.origin)
            : new URL(window.location.href);

          const isCheckoutAction =
            actionUrl.origin === window.location.origin &&
            actionUrl.pathname &&
            actionUrl.pathname.endsWith("/checkout");

          const hasCheckoutControl = !!form.querySelector(
            '[name="checkout"], button[name="checkout"], [type="submit"][name="checkout"]',
          );

          if (!isCheckoutAction && !hasCheckoutControl) return;

          const siteCart = document.querySelector("site-cart");
          if (!siteCart) return;

          e.preventDefault();
          e.stopPropagation();

          const submitEl =
            form.querySelector('button[type="submit"]') ||
            form.querySelector('input[type="submit"]') ||
            null;

          await siteCart.beginCheckout(
            `${window.location.origin}/checkout`,
            submitEl,
          );
        } catch (err) {
          console.error("Checkout guard submit error:", err);
        }
      },
      { capture: true },
    );
  }

  setCheckoutLinkBusy(linkEl, busy) {
    if (!linkEl) return () => {};

    const prev = {
      ariaDisabled: linkEl.getAttribute("aria-disabled"),
      ariaBusy: linkEl.getAttribute("aria-busy"),
      tabIndex: linkEl.getAttribute("tabindex"),
      pointerEvents: linkEl.style.pointerEvents,
      opacity: linkEl.style.opacity,
      cursor: linkEl.style.cursor,
    };

    if (busy) {
      linkEl.setAttribute("aria-disabled", "true");
      linkEl.setAttribute("aria-busy", "true");
      linkEl.setAttribute("tabindex", "-1");
      linkEl.style.pointerEvents = "none";
      linkEl.style.opacity = "0.7";
      linkEl.style.cursor = "progress";
    }

    return () => {
      if (busy) {
        if (prev.ariaDisabled === null) linkEl.removeAttribute("aria-disabled");
        else linkEl.setAttribute("aria-disabled", prev.ariaDisabled);

        if (prev.ariaBusy === null) linkEl.removeAttribute("aria-busy");
        else linkEl.setAttribute("aria-busy", prev.ariaBusy);

        if (prev.tabIndex === null) linkEl.removeAttribute("tabindex");
        else linkEl.setAttribute("tabindex", prev.tabIndex);

        linkEl.style.pointerEvents = prev.pointerEvents || "";
        linkEl.style.opacity = prev.opacity || "";
        linkEl.style.cursor = prev.cursor || "";
      }
    };
  }

  sleep(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  getCartSignature(cart) {
    if (!cart || !Array.isArray(cart.items)) return "";
    return cart.items
      .map((i) => {
        const r = i && i.properties ? i.properties._reward : "";
        const m =
          i && i.properties
            ? i.properties._entries_multiplier_product ||
              i.properties.entries_multiplier_product ||
              ""
            : "";
        return `${i.key}:${i.quantity}:${i.final_line_price}:${r}:${m}`;
      })
      .join("|");
  }

  hasEntriesMultiplierProperty(item) {
    if (!item || !item.properties) return false;
    return (
      item.properties._entries_multiplier_product === "1" ||
      item.properties.entries_multiplier_product === "1"
    );
  }

  async fetchCartStable({ attempts = 3, delayMs = 150 } = {}) {
    let last = null;
    let lastSig = null;

    for (let i = 0; i < attempts; i++) {
      const cart = await this.fetchCart();
      const sig = this.getCartSignature(cart);
      if (lastSig !== null && sig === lastSig) return cart;
      last = cart;
      lastSig = sig;
      if (i < attempts - 1) await this.sleep(delayMs);
    }

    return last;
  }

  buildUnifiedRewardOptions() {
    return {
      awaitReconciliation: true,
      rewardMutationContext: {
        skipSectionRender: true,
        skipRewardRefresh: true,
        cartBusy: false,
      },
      reconciliationOptions: {
        forceCartRefresh: true,
        skipBusyState: true,
        skipSectionRender: true,
        skipRewardRefresh: true,
        cartBusy: false,
      },
    };
  }

  getRewardThresholdsFromDom() {
    const milestones = Array.from(
      document.querySelectorAll('cart-rewards .milestone[data-type="product"]'),
    );
    const map = new Map();

    milestones.forEach((m) => {
      const variantId = Number(m.dataset.variantId);
      const threshold = Number(m.dataset.threshold);
      if (
        !isNaN(variantId) &&
        variantId > 0 &&
        !isNaN(threshold) &&
        threshold > 0
      ) {
        map.set(variantId, threshold);
      }
    });

    return map;
  }

  async removeIneligibleRewards(cart) {
    const thresholds = this.getRewardThresholdsFromDom();
    let clamped = false;
    const allItems = cart && Array.isArray(cart.items) ? cart.items : [];

    for (const item of allItems) {
      if (
        item &&
        item.key &&
        item.quantity > 1 &&
        this.isQuantityLockedLine(item, thresholds)
      ) {
        await this.changeCartLine(
          { id: item.key, quantity: 1 },
          "Reward quantity clamp",
        );
        clamped = true;
      }
    }

    if (!thresholds || thresholds.size === 0) return clamped;
    const subtotal = this.getEligibleSubtotal(cart, thresholds);

    const rewardItems = (
      cart && Array.isArray(cart.items) ? cart.items : []
    ).filter((i) => this.isRewardCartItem(i, thresholds));

    const removableRewardItems = rewardItems.filter((item) => {
      const variantId = item.variant_id;
      const threshold = thresholds.get(variantId);

      return (
        typeof threshold === "number"
          ? subtotal < threshold
          : this.hasRewardProperty(item)
      );
    });

    for (const item of removableRewardItems) {
      if (item && item.key) {
        await this.changeCartLine(
          { id: item.key, quantity: 0 },
          "Reward remove",
        );
      }
    }

    return removableRewardItems.length > 0 || clamped;
  }

  async prepareCartForCheckout() {
    await this.rewardOperationQueue;

    // Try to avoid racing other cart AJAX changes by waiting for cart.js to stabilize.
    let cart = await this.fetchCartStable({ attempts: 3, delayMs: 150 });
    if (!cart) cart = await this.fetchCart();

    // Ensure any reward lines that are no longer eligible are removed before checkout.
    await this.removeIneligibleRewards(cart);
    await this.rewardOperationQueue;

    // One more pass after removals settle (covers rapid cart changes).
    const cartAfter = await this.fetchCartStable({ attempts: 2, delayMs: 150 });
    if (cartAfter) {
      await this.removeIneligibleRewards(cartAfter);
      await this.rewardOperationQueue;
      await this.syncMultiplierState(cartAfter);
    }
  }

  async beginCheckout(checkoutUrl, linkEl) {
    if (this.checkoutGuardActive) return;
    this.checkoutGuardActive = true;

    const restore = this.setCheckoutLinkBusy(linkEl, true);
    this.restoreCheckoutLinkBusy = restore;

    try {
      await this.prepareCartForCheckout();
      window.location.href = checkoutUrl;
    } catch (err) {
      console.error("Failed to prepare cart for checkout:", err);
      restore();
      this.restoreCheckoutLinkBusy = null;
      this.checkoutGuardActive = false;
      window.location.href = checkoutUrl;
    }
  }

  initRewardsOnLoad() {
    // Retry a few times so reward and multiplier state settle on refresh.
    const maxAttempts = 6;
    const baseDelayMs = 200;
    let attempts = 0;

    const tryHandle = () => {
      attempts += 1;
      const rewardsEl = this.querySelector("cart-rewards");
      if (rewardsEl) {
        this.refreshCartState({ skipBusyState: true });
        return;
      }
      if (attempts < maxAttempts) {
        setTimeout(tryHandle, baseDelayMs * attempts);
      }
    };

    tryHandle();
  }

  hasRewardProperty(item) {
    const props = item && item.properties ? item.properties : {};
    const v = props ? props._reward : undefined;
    return v === true || v === "true" || v === 1 || v === "1";
  }

  isRewardCartItem(item, thresholds) {
    if (this.hasRewardProperty(item)) return true;

    const variantId = Number(item && item.variant_id);
    if (!Number.isFinite(variantId) || variantId <= 0) return false;

    const map =
      thresholds instanceof Map
        ? thresholds
        : this.getRewardThresholdsFromDom();
    return map instanceof Map && map.has(variantId);
  }

  isQuantityLockedLine(item, thresholds) {
    if (!item) return false;
    if (this.isRewardCartItem(item, thresholds)) return true;
    const price =
      typeof item.final_line_price === "number"
        ? item.final_line_price
        : typeof item.line_price === "number"
          ? item.line_price
          : null;
    return price === 0;
  }

  getEligibleSubtotal(cart, thresholds) {
    if (!cart || !Array.isArray(cart.items)) return 0;
    const map =
      thresholds instanceof Map
        ? thresholds
        : this.getRewardThresholdsFromDom();
    return cart.items.reduce((acc, item) => {
      if (this.isRewardCartItem(item, map)) return acc;
      if (this.hasEntriesMultiplierProperty(item)) return acc;
      const price =
        typeof item.final_line_price === "number"
          ? item.final_line_price
          : typeof item.line_price === "number"
            ? item.line_price
            : 0;
      return acc + price;
    }, 0);
  }

  getMultiplierOffer() {
    return this.querySelector("[data-site-cart-multiplier]");
  }

  getMultiplierOptions() {
    const offer = this.getMultiplierOffer();
    return {
      variantId: Number(offer?.dataset?.multiplierVariantId || 0),
      defaultMultiplier: Number(offer?.dataset?.defaultMultiplier || 2),
      enabledByDefault: offer?.dataset?.enabledByDefault === "true",
      root: offer || document.querySelector("[data-entries-cart-block]") || null,
    };
  }

  getMultiplierConfig() {
    const { variantId, defaultMultiplier, enabledByDefault } =
      this.getMultiplierOptions();
    if (!variantId || variantId <= 0) return null;

    return {
      enabled: true,
      multiplierValue: Math.max(1, defaultMultiplier || 1),
      multiplierProductVariantId: variantId,
      enabledByDefault,
    };
  }

  getMultiplierLine(cart) {
    if (!cart || !Array.isArray(cart.items)) return null;
    return cart.items.find((item) => this.hasEntriesMultiplierProperty(item)) || null;
  }

  hasEligiblePaidItems(cart) {
    const thresholds = this.getRewardThresholdsFromDom();
    if (!cart || !Array.isArray(cart.items)) return false;
    return cart.items.some((item) => {
      if (this.hasEntriesMultiplierProperty(item)) return false;
      if (this.isRewardCartItem(item, thresholds)) return false;
      return true;
    });
  }

  isMultiplierOptedIn(cart) {
    const attributes = cart?.attributes || {};
    return attributes.entries_multiplier_opt_in === "1";
  }

  async applyMultiplier(cart) {
    const config = this.getMultiplierConfig();
    if (!config || !config.enabled) return cart;

    const variantId = config.multiplierProductVariantId;
    const multiplier = config.multiplierValue;
    let current = cart || (await this.fetchCart());
    if (!current) return cart;

    const existingLine = this.getMultiplierLine(current);
    if (!existingLine) {
      const added = await this.addCartItems(
        [
          {
            id: variantId,
            quantity: 1,
            properties: { _entries_multiplier_product: "1" },
          },
        ],
        "Multiplier add",
      );
      if (added && !added.errors && !added.status) {
        current = added;
      } else {
        current = (await this.fetchCart()) || current;
      }
    }

    const updated = await this.updateCartPayload(
      {
        attributes: {
          entries_multiplier_opt_in: "1",
          entries_multiplier: String(multiplier),
          entries_multiplier_product_variant_id: String(variantId),
        },
      },
      "Multiplier opt-in",
    );
    if (updated && !updated.errors && !updated.status) {
      return (await this.fetchCart()) || updated;
    }
    return current;
  }

  async removeMultiplier(cart) {
    let current = cart || (await this.fetchCart());
    if (!current) return cart;

    const multiplierLine = this.getMultiplierLine(current);
    if (multiplierLine?.key) {
      const changed = await this.changeCartLine(
        { id: multiplierLine.key, quantity: 0 },
        "Multiplier remove",
      );
      if (changed && !changed.errors && !changed.status) {
        current = (await this.fetchCart()) || changed;
      }
    }

    const updated = await this.updateCartPayload(
      {
        attributes: {
          entries_multiplier_opt_in: "0",
        },
      },
      "Multiplier opt-out",
    );
    if (updated && !updated.errors && !updated.status) {
      return (await this.fetchCart()) || updated;
    }
    return current;
  }

  async syncMultiplierState(cart, optIn) {
    const config = this.getMultiplierConfig();
    if (!config) return { cart, applied: false, config: null };

    let current = cart || (await this.fetchCart());
    if (!current) return { cart, applied: false, config };

    const optedIn = this.isMultiplierOptedIn(current);
    const hasLine = Boolean(this.getMultiplierLine(current));
    const hasEligibleItems = this.hasEligiblePaidItems(current);

    if (optIn === true) {
      if (!hasEligibleItems) {
        current = await this.removeMultiplier(current);
        return { cart: current, applied: false, config };
      }
      current = await this.applyMultiplier(current);
      return {
        cart: current,
        applied: this.isMultiplierOptedIn(current) && Boolean(this.getMultiplierLine(current)),
        config,
      };
    }

    if (optIn === false) {
      current = await this.removeMultiplier(current);
      return { cart: current, applied: false, config };
    }

    if (optedIn && !hasEligibleItems) {
      current = await this.removeMultiplier(current);
      return { cart: current, applied: false, config };
    }

    if (optedIn && !hasLine && hasEligibleItems) {
      current = await this.applyMultiplier(current);
      return {
        cart: current,
        applied: this.isMultiplierOptedIn(current) && Boolean(this.getMultiplierLine(current)),
        config,
      };
    }

    return {
      cart: current,
      applied: optedIn && hasLine,
      config,
    };
  }

  computeBaseEntriesFromCart(cart) {
    if (!cart || !Array.isArray(cart.items)) return 0;
    const domEntries = new Map();

    document.querySelectorAll("[data-product-id][data-entries-base]").forEach((el) => {
      const productId = el.dataset.productId;
      const entries = Number(el.dataset.entriesBase || 0);
      if (productId && entries > 0) domEntries.set(String(productId), entries);
    });

    return cart.items.reduce((total, item) => {
      if (this.hasEntriesMultiplierProperty(item)) return total;
      const props = item?.properties || {};
      const lineEntries = Number(
        props._entries_base_each || props.entries_base_each || 0,
      );
      const fallbackEntries = domEntries.get(String(item?.product_id || "")) || 0;
      const entriesEach = lineEntries || fallbackEntries;
      const quantity = Math.max(1, Number(item?.quantity || 1));
      return total + entriesEach * quantity;
    }, 0);
  }

  async updateMultiplierOffer(cart, multiplierResult = null) {
    const offer = this.getMultiplierOffer();
    if (!offer) return;

    const config = multiplierResult?.config || this.getMultiplierConfig();
    const attributes = cart?.attributes || {};
    const hasMultiplierLine = Boolean(this.getMultiplierLine(cart));
    const applied =
      Boolean(multiplierResult?.applied) ||
      (this.isMultiplierOptedIn(cart) && hasMultiplierLine);
    const available = config ? Boolean(config.enabled) : false;
    const multiplier = Number(
      config?.multiplierValue || attributes.entries_multiplier || 1,
    );
    const baseEntries =
      Number(attributes.entries_base_total || 0) ||
      this.computeBaseEntriesFromCart(cart);
    const projectedEntries = baseEntries * Math.max(1, multiplier);

    offer.dataset.state = applied ? "applied" : "offer";
    offer.dataset.entriesMultiplierState = offer.dataset.state;
    offer.setAttribute(
      "data-entries-multiplier-available",
      available ? "true" : "false",
    );
    offer.classList.toggle("hidden", !available);

    const setText = (selector, text = "") => {
      const node = offer.querySelector(selector);
      if (node) node.textContent = text;
    };
    const clearOfferText = () => {
      setText("[data-entries-cart-kicker]");
      setText("[data-entries-cart-body-before]");
      setText("[data-entries-cart-projected-entries]");
      setText("[data-entries-cart-body-after]");
    };

    if (!available) {
      clearOfferText();
      setText("[data-entries-cart-multiple-summary]");
      this.bindMultiplierOffer(offer);
      return;
    }

    const n = String(Math.max(1, multiplier));
    if (applied) {
      clearOfferText();
      const appliedTemplate = offer.dataset.multiplierAppliedTemplate || "";
      setText(
        "[data-entries-cart-multiple-summary]",
        appliedTemplate ? appliedTemplate.replace(/\{n\}/g, n) : "",
      );
    } else {
      setText("[data-entries-cart-multiple-summary]");
      const kickerTemplate = offer.dataset.offerKickerTemplate || "";
      setText(
        "[data-entries-cart-kicker]",
        kickerTemplate ? kickerTemplate.replace(/\{n\}/g, n) : "",
      );

      const bodyTemplate = offer.dataset.offerBodyTemplate || "";
      const marker = "{entries}";
      const markerIndex = bodyTemplate.indexOf(marker);
      if (markerIndex === -1) {
        setText("[data-entries-cart-body-before]", bodyTemplate);
        setText("[data-entries-cart-projected-entries]");
        setText("[data-entries-cart-body-after]");
      } else {
        setText("[data-entries-cart-body-before]", bodyTemplate.slice(0, markerIndex));
        setText("[data-entries-cart-projected-entries]", String(projectedEntries));
        setText(
          "[data-entries-cart-body-after]",
          bodyTemplate.slice(markerIndex + marker.length),
        );
      }
    }

    this.bindMultiplierOffer(offer);
  }

  bindMultiplierOffer(offer = this.getMultiplierOffer()) {
    if (!offer || offer.dataset.bound === "1") return;
    offer.dataset.bound = "1";

    const accept = offer.querySelector("[data-entries-cart-accept], [data-multiplier-accept]");
    const cancel = offer.querySelector("[data-entries-cart-cancel], [data-multiplier-cancel]");

    if (accept) {
      accept.addEventListener("click", () => {
        this.runCartTransaction(() => this.fetchCart(), {
          multiplierOptIn: true,
        });
      });
    }

    if (cancel) {
      cancel.addEventListener("click", () => {
        this.runCartTransaction(() => this.fetchCart(), {
          multiplierOptIn: false,
        });
      });
    }
  }

  async settleRewards(cart) {
    let current = cart || (await this.fetchCart());
    const rewards = this.querySelector("cart-rewards");
    const thresholds = this.getRewardThresholdsFromDom();
    if (!current || !Array.isArray(current.items) || !rewards || !thresholds.size) {
      return current;
    }

    // Most cart interactions don't shift any rewards; skip the multi-pass loop
    // (and its refetches) when nothing needs to be added or removed.
    if (!this.getRewardMutationState(current, thresholds, rewards).needsRewardMutation) {
      return current;
    }

    for (let pass = 0; pass < 3; pass += 1) {
      let changed = false;
      const subtotal = this.getEligibleSubtotal(current, thresholds);
      const rewardItems = current.items.filter((item) =>
        this.isRewardCartItem(item, thresholds),
      );

      for (const item of rewardItems) {
        const threshold = thresholds.get(item.variant_id);
        const shouldRemove =
          typeof threshold === "number"
            ? subtotal < threshold
            : this.hasRewardProperty(item);

        if (shouldRemove && item.key) {
          current = await this.changeCartLine(
            { id: item.key, quantity: 0 },
            "Reward remove",
          );
          changed = true;
        }
      }

      if (changed) {
        current = (await this.fetchCart()) || current;
        continue;
      }

      const existingRewardIds = new Set(
        current.items
          .filter((item) => this.isRewardCartItem(item, thresholds))
          .map((item) => Number(item.variant_id)),
      );
      const autoAddItems = Array.from(
        rewards.querySelectorAll('.milestone[data-type="product"][data-auto-add="true"]'),
      ).reduce((items, milestone) => {
        const variantId = Number(milestone.dataset.variantId);
        const threshold = Number(milestone.dataset.threshold);
        const shouldAdd =
          Number.isFinite(variantId) &&
          Number.isFinite(threshold) &&
          subtotal >= threshold &&
          !existingRewardIds.has(variantId);
        if (!shouldAdd) return items;

        const properties = { _reward: "true" };
        if (milestone.dataset.entriesBase) {
          properties._entries_base_each = milestone.dataset.entriesBase;
        }
        items.push({
          id: variantId,
          quantity: 1,
          properties,
        });
        existingRewardIds.add(variantId);
        return items;
      }, []);

      if (autoAddItems.length) {
        const nextCart = await this.addCartItems(autoAddItems, "Reward add");
        if (!nextCart || !Array.isArray(nextCart.items)) {
          console.warn("Reward add did not return a cart; keeping previous cart state.");
          current = (await this.fetchCart()) || current;
          break;
        }
        current = nextCart;
        changed = true;
      }

      if (!changed) break;
    }

    return current;
  }

  emitCartUpdated(cart) {
    this.updateCartToggleLabel(cart);
    const detail = { source: "site-cart", cart };
    document.dispatchEvent(new CustomEvent("cart:updated", { detail }));
    window.dispatchEvent(new CustomEvent("cart:updated", { detail }));
  }

  updateCartToggleLabel(cart) {
    const count =
      cart && typeof cart.item_count === "number" ? cart.item_count : null;
    const strings = window.themeLocalization?.strings || {};
    let label = strings.openCart || "Open cart";
    if (count === 1) label = strings.openCartOneItem || "Open cart, 1 item";
    else if (count && count > 1)
      label = (strings.openCartOtherItems || "Open cart, {count} items").replace(
        "{count}",
        count,
      );
    document.querySelectorAll("cart-toggle button").forEach((btn) => {
      btn.setAttribute("aria-label", label);
    });
  }

  enqueueRewardOperation(fn) {
    this.rewardOperationQueue = this.rewardOperationQueue
      .then(() => fn())
      .catch((e) => {
        console.error("Reward operation failed:", e);
      });
    return this.rewardOperationQueue;
  }

  // On /cart the cart is the page itself: there is no drawer to open or close.
  get isPageMode() {
    return this.dataset.cartMode === "page";
  }

  openCart() {
    if (this.isPageMode) return;
    if (this.cart) {
      this.cart.classList.add("active");
      document.body.style.overflow = "hidden";
    }
  }

  closeCart() {
    if (this.isPageMode) return;
    if (this.cart) {
      this.cart.classList.remove("active");
      document.body.removeAttribute("style");
    }
  }

  toggleCart() {
    if (this.isPageMode) {
      // The header cart icon scrolls back up to the cart instead.
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    if (this.cart.classList.contains("active")) {
      this.closeCart();
    } else {
      this.openCart();
    }
  }

  getSectionTargets(selector) {
    return Array.from(document.querySelectorAll(selector));
  }

  setBusyState(target, busy) {
    if (!target) return;

    target.classList.toggle("is-loading", busy);

    if (busy) {
      target.setAttribute("aria-busy", "true");
    } else {
      target.removeAttribute("aria-busy");
    }
  }

  setControlsBusy(controls, busy) {
    controls.forEach((control) => {
      if (!control || typeof control.disabled === "undefined") return;

      if (busy) {
        control.dataset.wasDisabled = control.disabled ? "true" : "false";
        control.disabled = true;
        return;
      }

      const wasDisabled = control.dataset.wasDisabled === "true";
      control.disabled = wasDisabled;
      delete control.dataset.wasDisabled;
    });
  }

  setCartBusy(busy) {
    if (this.cart) {
      this.setBusyState(this.cart, busy);
    }

    ["#main-cart-items", "#main-cart-footer"].forEach((selector) => {
      this.getSectionTargets(selector).forEach((target) => {
        this.setBusyState(target, busy);
      });
    });
  }

  setAddToCartBusy(cartAdd, submitter, busy) {
    if (!(cartAdd instanceof HTMLElement)) return;

    this.setBusyState(cartAdd, busy);

    const button =
      submitter ||
      cartAdd.querySelector(
        'button[type="submit"], button:not([type]), input[type="submit"]',
      );

    if (!button) return;

    this.setControlsBusy([button], busy);

    const addingLabel =
      window.themeLocalization?.strings?.adding || "Adding...";

    if (button.tagName === "INPUT") {
      if (busy) {
        if (!button.dataset.originalValue) {
          button.dataset.originalValue = button.value;
        }
        button.value = addingLabel;
      } else if (button.dataset.originalValue) {
        button.value = button.dataset.originalValue;
        delete button.dataset.originalValue;
      }

      return;
    }

    const label = button.querySelector("span");

    if (busy) {
      if (!button.dataset.originalLabel) {
        button.dataset.originalLabel = label
          ? label.textContent
          : button.textContent;
      }

      if (label) {
        label.textContent = addingLabel;
      } else {
        button.textContent = addingLabel;
      }

      return;
    }

    if (!button.dataset.originalLabel) return;

    if (label) {
      label.textContent = button.dataset.originalLabel;
    } else {
      button.textContent = button.dataset.originalLabel;
    }

    delete button.dataset.originalLabel;
  }

  async runCartMutation(fn, context = {}) {
    const {
      cartAdd = null,
      submitter = null,
      cartBusy = true,
    } = context;

    this.pendingCartMutations += 1;
    if (cartBusy) {
      this.pendingCartBusyMutations += 1;
      this.setCartBusy(true);
    }

    if (cartAdd) {
      this.setAddToCartBusy(cartAdd, submitter, true);
    }

    try {
      return await fn();
    } finally {
      if (cartAdd) {
        this.setAddToCartBusy(cartAdd, submitter, false);
      }

      this.pendingCartMutations = Math.max(this.pendingCartMutations - 1, 0);
      if (cartBusy) {
        this.pendingCartBusyMutations = Math.max(
          this.pendingCartBusyMutations - 1,
          0,
        );
      }
      this.setCartBusy(this.pendingCartBusyMutations > 0);
    }
  }

  async runCartTransaction(fn, context = {}) {
    const run = async () =>
      this.runCartMutation(async () => {
        const result = await fn();
        if (result && (result.status || result.errors)) return result;

        let cart = this.getCartFromMutationResponse(result) || result;
        if (!cart || !Array.isArray(cart.items)) cart = await this.fetchCart();

        // The mutation can return the rendered cart sections alongside the cart,
        // letting us skip a second round trip. Only reuse that HTML when rewards
        // won't shift — a reward add/remove would make it stale.
        const mutationSectionsHtml = this.getSectionsHtmlFromResponse(result);
        const rewardsWillChange = this.getRewardMutationState(
          cart,
          this.getRewardThresholdsFromDom(),
        ).needsRewardMutation;

        // Each step is guarded so one failure (e.g. a 403 on cart.js) can't
        // cascade into a stale UI — later steps still run with the last good cart.
        try {
          cart = (await this.settleRewards(cart)) || cart;
        } catch (e) {
          console.warn("settleRewards failed", e);
        }

        let multiplierResult = null;
        try {
          multiplierResult = await this.syncMultiplierState(
            cart,
            context.multiplierOptIn,
          );
          if (multiplierResult && multiplierResult.cart) {
            cart = multiplierResult.cart;
          }
        } catch (e) {
          console.warn("multiplier sync failed", e);
        }

        try {
          if (mutationSectionsHtml && !rewardsWillChange) {
            this.applyCartSectionsHtml(mutationSectionsHtml);
          } else {
            await this.renderRefreshedCartSections();
          }
        } catch (e) {
          console.warn("section render failed", e);
        }

        // The section render already returns fresh cart-derived HTML and the
        // steps above hand back an up-to-date cart object, so reuse it instead
        // of an extra cart.js round trip. Only refetch if we somehow lost it.
        const finalCart =
          cart && Array.isArray(cart.items) ? cart : await this.fetchCart();

        try {
          await this.handleRewards({
            cart: finalCart,
            skipRewardMutations: true,
          });
        } catch (e) {
          console.warn("handleRewards failed", e);
        }

        try {
          await this.updateMultiplierOffer(finalCart, multiplierResult);
        } catch (e) {
          console.warn("updateMultiplierOffer failed", e);
        }

        this.emitCartUpdated(finalCart);
        return finalCart;
      }, context);

    this.cartTransactionQueue = this.cartTransactionQueue
      .catch(() => {})
      .then(run);

    return this.cartTransactionQueue;
  }

  async refreshCartState(options = {}) {
    return this.runCartTransaction(() => this.fetchCart(), {
      cartBusy: !options.skipBusyState,
    });
  }

  getCartFromMutationResponse(data) {
    return data && Array.isArray(data.items) ? data : null;
  }

  async parseCartJson(response, requestLabel) {
    const contentType = response.headers.get("content-type") || "";
    const bodyText = await response.text();

    try {
      return JSON.parse(bodyText);
    } catch (_error) {}

    if (!response.ok) {
      console.warn(`${requestLabel} returned a non-JSON response`, {
        status: response.status,
        contentType,
      });
      return {
        status: response.status,
        errors: true,
        message: `${requestLabel} failed`,
      };
    }

    console.warn(`${requestLabel} returned an unexpected body`, {
      status: response.status,
      contentType,
    });
    return {
      status: response.status,
      errors: true,
      message: `${requestLabel} returned an unexpected body`,
    };
  }

  warnCartError(data, requestLabel) {
    if (!data || !(data.status || data.errors)) return;
    console.warn(
      `${requestLabel} error:`,
      data.description || data.message || data.errors || "Cart Error",
    );
  }

  async cartPost(endpoint, payload, requestLabel) {
    const response = await fetch(`${window.Shopify.routes.root}${endpoint}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify(payload),
    });
    const data = await this.parseCartJson(response, requestLabel);
    this.warnCartError(data, requestLabel);
    return data;
  }

  async addCartItems(items, requestLabel = "Cart add", { withSections = false } = {}) {
    try {
      const body = withSections ? { items, sections: "site-cart" } : { items };
      const data = await this.cartPost("cart/add.js", body, requestLabel);
      if (data && (data.status || data.errors)) return data;
      const cart = await this.fetchCart();
      // add.js doesn't return the full cart, so carry the rendered sections
      // (when requested) onto the fetched cart for the transaction to reuse.
      if (cart && withSections && data && data.sections) {
        cart.sections = data.sections;
      }
      return cart;
    } catch (error) {
      console.warn(`${requestLabel} error:`, error);
      return null;
    }
  }

  async changeCartLine(payload, requestLabel = "Cart change", { withSections = false } = {}) {
    const body = withSections ? { ...payload, sections: "site-cart" } : payload;
    return this.cartPost("cart/change.js", body, requestLabel);
  }

  async updateCartPayload(payload, requestLabel = "Cart update", { withSections = false } = {}) {
    const body = withSections ? { ...payload, sections: "site-cart" } : payload;
    return this.cartPost("cart/update.js", body, requestLabel);
  }

  getRewardMutationState(cart, thresholds, rewards = null) {
    if (!cart || !Array.isArray(cart.items)) {
      return {
        needsRewardMutation: false,
      };
    }

    const thresholdMap =
      thresholds instanceof Map ? thresholds : this.getRewardThresholdsFromDom();
    if (!thresholdMap || thresholdMap.size === 0) {
      return {
        needsRewardMutation: false,
      };
    }

    const subtotal = this.getEligibleSubtotal(cart, thresholdMap);
    const rewardItems = cart.items.filter((item) =>
      this.isRewardCartItem(item, thresholdMap),
    );

    const hasPendingRemoval = rewardItems.some((item) => {
      const threshold = thresholdMap.get(item.variant_id);
      return typeof threshold === "number"
        ? subtotal < threshold
        : this.hasRewardProperty(item);
    });
    if (hasPendingRemoval) {
      return {
        needsRewardMutation: true,
      };
    }

    const rewardsEl = rewards || this.querySelector("cart-rewards");
    if (!rewardsEl) {
      return {
        needsRewardMutation: false,
      };
    }

    const hasPendingAutoAdd = Array.from(
      rewardsEl.querySelectorAll('.milestone[data-type="product"]'),
    ).some((milestone) => {
      if (milestone.dataset.autoAdd !== "true") return false;

      const variantId = Number(milestone.dataset.variantId);
      const threshold = Number(milestone.dataset.threshold);
      if (
        !Number.isFinite(variantId) ||
        variantId <= 0 ||
        !Number.isFinite(threshold) ||
        threshold <= 0
      ) {
        return false;
      }

      const rewardItem = rewardItems.find(
        (item) => Number(item && item.variant_id) === variantId,
      );
      return subtotal >= threshold && !rewardItem;
    });

    return {
      needsRewardMutation: hasPendingAutoAdd,
    };
  }

  async addVariantToCart(id, quantity = 1, options, context = {}) {
    const items = [options ? { id, quantity, ...options } : { id, quantity }];

    if (this.cart && !this.isPageMode && !this.cart.classList.contains("active")) {
      this.toggleCart();
    }

    return this.runCartTransaction(
      () => this.addCartItems(items, "Cart add", { withSections: true }),
      context,
    );
  }

  async removeItemByKey(key, context = {}) {
    return this.runCartTransaction(
      () =>
        this.changeCartLine(
          { id: key, quantity: 0 },
          "Cart change",
          { withSections: true },
        ),
      context,
    );
  }

  async updateQuantity(line, quantity, context = {}) {
    const isKey =
      typeof line === "string" && line !== "" && isNaN(Number(line.trim()));
    const payload = isKey
      ? { id: line, quantity }
      : { line, quantity };
    const mutationContext = {
      ...context,
      // Quantity stepper actions should still show the shared cart loader
      // now that row-level item spinners have been removed.
      cartBusy: context.cartBusy ?? true,
    };

    return this.runCartTransaction(async () => {
      // If removing a paid item would leave only reward items, remove rewards first
      // so cart mutations aren't blocked by checkout/cart validations.
      if (Number(quantity) === 0) {
        try {
          const cart = await this.fetchCartStable({
            attempts: 2,
            delayMs: 120,
          });
          const thresholds = this.getRewardThresholdsFromDom();

          const items = cart && Array.isArray(cart.items) ? cart.items : [];
          const rewardItems = items.filter((i) =>
            this.isRewardCartItem(i, thresholds),
          );

          const isRemovingKey = isKey && typeof line === "string";
          const removingItem = items.find((i) => {
            if (!i) return false;
            if (isRemovingKey) return i.key === line;
            const idx = Number(line);
            return Number.isFinite(idx) && idx > 0 && i.index + 1 === idx;
          });

          const removingIsReward = removingItem
            ? this.isRewardCartItem(removingItem, thresholds)
            : false;
          const eligibleSubtotal = this.getEligibleSubtotal(cart, thresholds);
          const removingPrice =
            removingItem && typeof removingItem.final_line_price === "number"
              ? removingItem.final_line_price
              : 0;
          const eligibleAfter = removingIsReward
            ? eligibleSubtotal
            : eligibleSubtotal - removingPrice;

          if (!removingIsReward && rewardItems.length && eligibleAfter <= 0) {
            for (const rewardItem of rewardItems) {
              if (rewardItem && rewardItem.key) {
                await this.changeCartLine(
                  { id: rewardItem.key, quantity: 0 },
                  "Reward remove",
                );
              }
            }
          }
        } catch (e) {
          // Best-effort; fall through to original mutation.
        }
      }

      const requestedQty = Number(quantity);
      if (requestedQty > 1) {
        try {
          const guardCart = await this.fetchCartStable({
            attempts: 2,
            delayMs: 120,
          });
          const thresholds = this.getRewardThresholdsFromDom();
          const guardItems =
            guardCart && Array.isArray(guardCart.items) ? guardCart.items : [];
          const targetItem = guardItems.find((i) => {
            if (!i) return false;
            if (isKey && typeof line === "string") return i.key === line;
            const idx = Number(line);
            return Number.isFinite(idx) && idx > 0 && i.index + 1 === idx;
          });

          if (
            targetItem &&
            this.isQuantityLockedLine(targetItem, thresholds)
          ) {
            if (targetItem.quantity === 1) {
              return guardCart;
            }

            const clampPayload = isKey
              ? { id: line, quantity: 1 }
              : { line, quantity: 1 };
            return this.changeCartLine(
              clampPayload,
              "Reward quantity clamp",
              { withSections: true },
            );
          }
        } catch (e) {
          // Best-effort; fall through to original mutation.
        }
      }

      return this.changeCartLine(payload, "Cart change", { withSections: true });
    }, mutationContext);
  }

  async updateCart(updates, context = {}) {
    return this.runCartTransaction(
      () =>
        this.updateCartPayload({ updates }, "Cart update", {
          withSections: true,
        }),
      context,
    );
  }

  async fetchCart() {
    const uri = window.Shopify.routes.root + "cart.js";
    const resp = await fetch(uri, {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    });
    if (!resp.ok) return null;
    const data = await this.parseCartJson(resp, "Cart fetch");
    return data && !data.errors ? data : null;
  }

  async handleRewards(options = {}) {
    const rewards = this.querySelector("cart-rewards");
    if (!rewards) {
      return { needsRewardMutation: false };
    }

    const cart =
      options.cart && Array.isArray(options.cart.items)
        ? options.cart
        : await this.fetchCart();
    if (!cart || !Array.isArray(cart.items)) {
      return { needsRewardMutation: false };
    }
    const thresholds = this.getRewardThresholdsFromDom();
    const subtotal = this.getEligibleSubtotal(cart, thresholds);

    this.updateShippingStatus(rewards, subtotal);
    const queuedRewardOperation = this.updateUnifiedBar(
      rewards,
      cart,
      subtotal,
      options,
    );
    if (options.skipRewardMutations) {
      return { needsRewardMutation: false };
    }
    const rewardState = this.getRewardMutationState(cart, thresholds, rewards);
    const needsRewardMutation =
      queuedRewardOperation || rewardState.needsRewardMutation;

    if (
      options.awaitReconciliation ||
      (options.awaitReconciliationIfNeeded && needsRewardMutation)
    ) {
      await this.reconcileRewards(options.reconciliationOptions || {});
      return { needsRewardMutation };
    }
    if (needsRewardMutation) {
      this.scheduleRewardReconciliation();
    }
    return { needsRewardMutation };
  }

  updateShippingStatus(rewards, subtotal) {
    const shippingThreshold = Number(rewards.dataset.shippingThreshold);
    if (!shippingThreshold || shippingThreshold <= 0) return;

    const shippingStatuses = rewards.querySelectorAll(
      "[data-shipping-status] [data-status]",
    );

    // Hide all statuses first
    shippingStatuses.forEach((status) => status.classList.add("hidden"));

    if (subtotal === 0) {
      const emptyStatus = rewards.querySelector(
        '[data-shipping-status] [data-status="empty"]',
      );
      if (emptyStatus) emptyStatus.classList.remove("hidden");
    } else if (subtotal >= shippingThreshold) {
      const earnedStatus = rewards.querySelector(
        '[data-shipping-status] [data-status="earned"]',
      );
      if (earnedStatus) earnedStatus.classList.remove("hidden");
    } else {
      const pendingStatus = rewards.querySelector(
        '[data-shipping-status] [data-status="pending"]',
      );
      if (pendingStatus) {
        const remaining = shippingThreshold - subtotal;
        const remainingSpan = pendingStatus.querySelector(
          ".shipping-remaining",
        );
        if (remainingSpan) {
          const formatter = new Intl.NumberFormat("en-US", {
            style: "currency",
            currency: "USD",
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          });
          const formattedValue = formatter.format(remaining / 100);
          remainingSpan.textContent = formattedValue.replace(".00", "");
        }
        pendingStatus.classList.remove("hidden");
      }
    }
  }

  updateUnifiedBar(rewards, cart, subtotal, options = {}) {
    const milestones = Array.from(rewards.querySelectorAll(".milestone"));
    const priceBubbles = Array.from(rewards.querySelectorAll(".price-bubble"));
    if (!milestones.length) return false;
    const rewardMutationContext = options.rewardMutationContext || {};
    let queuedRewardOperation = false;

    const thresholdsMap = this.getRewardThresholdsFromDom();
    const thresholds = milestones
      .map((m) => Number(m.dataset.threshold))
      .filter((t) => !isNaN(t) && t > 0);

    if (thresholds.length === 0) return false;

    const maxThreshold = Math.max(...thresholds);

    const bar = rewards.querySelector(".percentage-bar");
    if (bar) {
      const width = Math.min((subtotal / maxThreshold) * 100, 100);
      // The bar fills its clipping wrapper, so sliding it left by the
      // remainder keeps the fill on the compositor instead of laying out a width.
      bar.style.transform = `translateX(${width - 100}%)`;
    }

    // Group milestones by threshold to handle collisions
    const milestonesByThreshold = new Map();
    milestones.forEach((m) => {
      const threshold = Number(m.dataset.threshold);
      if (!isNaN(threshold) && threshold > 0) {
        if (!milestonesByThreshold.has(threshold)) {
          milestonesByThreshold.set(threshold, []);
        }
        milestonesByThreshold.get(threshold).push(m);
      }
    });

    // Position price bubbles to match their milestones
    priceBubbles.forEach((bubble) => {
      const threshold = Number(bubble.dataset.threshold);
      if (!isNaN(threshold) && threshold > 0) {
        const leftPct = (threshold / maxThreshold) * 100;
        bubble.style.left = `${leftPct}%`;

        const isEarned = subtotal >= threshold;
        const milestoneWrapper = bubble.querySelector(".milestone-bubble");

        if (milestoneWrapper) {
          if (isEarned) {
            milestoneWrapper.classList.add("active");
          } else {
            milestoneWrapper.classList.remove("active");
          }
        }
      }
    });

    // Position each milestone and handle its state
    milestonesByThreshold.forEach((group, threshold) => {
      const leftPct = (threshold / maxThreshold) * 100;

      // Stack vertically if multiple milestones at same threshold
      group.forEach((m, index) => {
        m.style.left = `${leftPct - 10}%`;

        // Offset vertically for collision handling (stagger up)
        if (group.length > 1) {
          const offsetTop = index * 16; // 16px spacing between stacked items
          m.style.top = `${offsetTop}px`;
        } else {
          m.style.top = "0px";
        }

        const type = m.dataset.type;
        const isEarned = subtotal >= threshold;

        const icon = m.querySelector(".milestone-icon");
        if (icon) {
          if (isEarned) {
            icon.classList.add("active");
          } else {
            icon.classList.remove("active");
          }
        }

        // Handle product rewards auto-add/remove logic
        if (type === "product") {
          const variantId = Number(m.dataset.variantId);
          const autoAdd = m.dataset.autoAdd === "true";

          const rewardItem = cart.items.find((i) => {
            if (!i || i.variant_id !== variantId) return false;
            return this.isRewardCartItem(i, thresholdsMap);
          });
          const isInCart = !!rewardItem;

          const addBtn = m.querySelector(".add-reward-btn");
          if (isEarned && !autoAdd && !isInCart && addBtn) {
            addBtn.classList.remove("hidden");
          } else if (addBtn && (autoAdd || !isEarned || isInCart)) {
            addBtn.classList.add("hidden");
          }

          if (autoAdd && !options.skipRewardMutations) {
            const operationKey = `add_${variantId}`;

            if (isEarned && !isInCart) {
              // Check if we're already adding this reward
              if (this.pendingRewardOperations.has(operationKey)) {
                return;
              }

              this.pendingRewardOperations.add(operationKey);
              queuedRewardOperation = true;

              // Add reward product with special property
              const autoAddMilestone = document.querySelector(
                `cart-rewards .milestone[data-variant-id="${variantId}"]`,
              );
              const autoAddEntriesBase = autoAddMilestone?.dataset?.entriesBase;
              const autoAddProperties = { _reward: "true" };
              if (autoAddEntriesBase)
                autoAddProperties._entries_base_each = autoAddEntriesBase;
              this.enqueueRewardOperation(() =>
                this.addVariantToCart(
                  variantId,
                  1,
                  {
                    properties: autoAddProperties,
                  },
                  {
                    ...rewardMutationContext,
                    skipRewardRefresh: true,
                  },
                ),
              ).finally(() => {
                setTimeout(() => {
                  this.pendingRewardOperations.delete(operationKey);
                }, 500);
              });
            } else if (!isEarned && isInCart && rewardItem) {
              const removeKey = `remove_${rewardItem.key}`;

              // Check if we're already removing this reward
              if (this.pendingRewardOperations.has(removeKey)) {
                return;
              }

              this.pendingRewardOperations.add(removeKey);
              queuedRewardOperation = true;

              // Remove reward product when threshold no longer met
              this.enqueueRewardOperation(() =>
                this.removeItemByKey(rewardItem.key, {
                  ...rewardMutationContext,
                  skipRewardRefresh: true,
                }),
              ).finally(() => {
                setTimeout(() => {
                  this.pendingRewardOperations.delete(removeKey);
                }, 500);
              });
            }
          }
        }
      });
    });

    this.addRewardButtonListeners();
    return queuedRewardOperation;
  }

  addRewardButtonListeners() {
    const addButtons = this.querySelectorAll(".add-reward-btn");
    addButtons.forEach((button) => {
      if (button.dataset && button.dataset.rewardBound === "1") return;
      if (button.dataset) button.dataset.rewardBound = "1";
      button.addEventListener("click", (e) => {
        const target = e.currentTarget || button;
        const variantId =
          target && target.dataset ? target.dataset.variantId : null;
        if (variantId) {
          const btnMilestone = document.querySelector(
            `cart-rewards .milestone[data-variant-id="${variantId}"]`,
          );
          const btnEntriesBase = btnMilestone?.dataset?.entriesBase;
          const btnProperties = { _reward: "true" };
          if (btnEntriesBase) btnProperties._entries_base_each = btnEntriesBase;
          this.addVariantToCart(variantId, 1, { properties: btnProperties });
        }
      });
    });
  }
}

customElements.define("site-cart", SiteCart);
